'use client';

import { Check, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';
import { FC, memo, useCallback, useMemo, useState } from 'react';

import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Spinner,
  Tooltip,
  TooltipProvider,
} from '@ui/components';
import { cn } from '@ui/lib/utils';
import { useDebounce } from '@uidotdev/usehooks';

const ITEM_PER_PAGE = 200;

export interface CommandOption {
  label: string;
  value: string;
  disabled?: boolean;
}

interface ComboboxProps {
  label?: string;
  options: CommandOption[];
  value?: string | string[] | undefined | null;
  multi?: boolean;
  placeholder?: string;
  searchText?: string;
  notFoundText?: string;
  onChange?: (value: string) => void;
  onChangeMulti?: (value: string[]) => void;
  className?: string;
  disabled?: boolean;
  isSkeleton?: boolean;
  isLoading?: boolean;
  onSearch?: (value: string) => void;
  isSkeletonInside?: boolean;
}

export const Combobox: FC<ComboboxProps> = (props) => {
  const {
    options = [],
    value,
    multi = false,
    placeholder = 'Select',
    searchText = 'Search',
    notFoundText = 'No Options',
    onChange,
    onChangeMulti,
    className,
    disabled,
    label,
    isSkeleton,
    isLoading,
    onSearch,
    isSkeletonInside,
  } = props;
  const [open, setOpen] = React.useState(false);

  const [visibleOptions, setVisibleOptions] = useState(ITEM_PER_PAGE);

  const [filterValue, setFilterValue] = useState('');

  const debounceFilterValue = useDebounce(filterValue, 300);

  const selectedLabels = useMemo(() => {
    if (multi && Array.isArray(value)) {
      return options
        .filter((option) => value.includes(option.value))
        .map((option) => option.label)
        .join(', ');
    }
    const selectedOption = options.find((option) => option.value === value);
    return selectedOption?.label || '';
  }, [options, value, multi]);

  const filteredOptions = useMemo(() => {
    const lowerCaseFilter = debounceFilterValue.toLowerCase();
    return options.filter(
      (el) =>
        el?.value?.toLowerCase()?.includes(lowerCaseFilter) ||
        el?.label?.toLowerCase()?.includes(lowerCaseFilter),
    );
  }, [options, debounceFilterValue]);

  const displayedOptions = useMemo(() => {
    return filteredOptions.slice(0, visibleOptions);
  }, [filteredOptions, visibleOptions]);

  const handleSelect = useCallback(
    (currentValue: string) => {
      if (multi) {
        const selectedValues = Array.isArray(value) ? value : [];
        if (currentValue === 'all') {
          if (selectedValues.includes('all')) {
            return onChangeMulti?.(selectedValues.filter((el) => el !== 'all'));
          }
          return onChangeMulti?.(['all']);
        }
        if (currentValue === 'none') {
          return onChangeMulti?.([]);
        }
        const isSelected = selectedValues.includes(currentValue);
        const newValue = isSelected
          ? selectedValues.filter((val) => val !== currentValue)
          : [...selectedValues, currentValue];
        if (currentValue !== 'all' && selectedValues.includes('all')) {
          return onChangeMulti?.(newValue.filter((el) => el !== 'all'));
        }
        onChangeMulti?.(newValue);
      } else {
        if (currentValue === 'none') {
          onChange?.('');
        } else {
          onChange?.(currentValue === value ? '' : currentValue);
        }
        setOpen(false);
      }
    },
    [value, multi, onChange, onChangeMulti],
  );

  const handleOpen = useCallback(
    (value: boolean) => {
      if (disabled) return;
      setOpen(value);
    },
    [disabled],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setFilterValue(value);
      if (!onSearch) return;
      onSearch(value);
    },
    [onSearch],
  );

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setVisibleOptions((prev) => prev + ITEM_PER_PAGE);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <Popover
          open={open}
          onOpenChange={handleOpen}
        >
          {isSkeleton ? (
            <div className={cn('flex w-[200px] flex-col gap-2', className)}>
              {!!label && (
                <Label className="text-xs font-semibold">{label}</Label>
              )}
              <Skeleton
                className={cn('w-[200px] h-10 text-left rounded-md', className)}
              />
            </div>
          ) : (
            <PopoverTrigger
              className="flex flex-col gap-2"
              disabled={disabled}
              asChild
            >
              <div className={cn('flex w-[200px] flex-col gap-2', className)}>
                {!!label && (
                  <Label className="text-xs font-semibold">{label}</Label>
                )}
                <Button
                  aria-expanded={open}
                  className={cn('w-full h-10 justify-between pr-3')}
                  disabled={disabled}
                  role="combobox"
                  type="button"
                  variant="outline"
                >
                  <span className="truncate">
                    {selectedLabels || (
                      <span className="text-gray-600">{placeholder}</span>
                    )}
                  </span>
                  <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                </Button>
              </div>
            </PopoverTrigger>
          )}
          <PopoverContent
            className={cn(
              'w-[200px] max-w-[250px] overscroll-y-auto p-0 z-50',
              className,
            )}
          >
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={`${searchText}...`}
                value={filterValue}
                onValueChange={handleSearch}
              />
              <CommandList onScroll={handleScroll}>
                <CommandEmpty className="flex items-center justify-center">
                  {isLoading ? <Spinner className="size-6" /> : notFoundText}.
                </CommandEmpty>
                <CommandGroup>
                  {isSkeletonInside ? (
                    <>
                      {Array.from({ length: 4 }).map((_, index) => (
                        <CommandItem
                          key={`loading-${index}`}
                          className="pointer-events-none text-left"
                          disabled
                          value={`loading-${index}`}
                        >
                          <Skeleton className="mr-2 h-4 w-4 min-w-4 rounded" />
                          <Skeleton className="h-4 w-[160px] rounded" />
                        </CommandItem>
                      ))}
                    </>
                  ) : (
                    <>
                      {multi && (
                        <CommandItem
                          value="none"
                          onSelect={() => handleSelect('none')}
                          className="text-left"
                        >
                          <div className="w-4 mr-2"></div>
                          <span className="text-gray-500">None</span>
                        </CommandItem>
                      )}
                      {displayedOptions.map((el) => (
                        <MemoizedCommandItem
                          key={el.value}
                          disabled={!!el.disabled}
                          el={el}
                          handleSelect={handleSelect}
                          multi={multi}
                          value={value}
                        />
                      ))}
                    </>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </Tooltip>
    </TooltipProvider>
  );
};

interface MemoizedCommandItemProps {
  el: CommandOption;
  value?: string | string[] | undefined | null;
  multi: boolean;
  disabled: boolean;
  handleSelect: (value: string) => void;
}

const MemoizedCommandItem: FC<MemoizedCommandItemProps> = memo(
  ({ el, value, multi, disabled, handleSelect }: MemoizedCommandItemProps) => (
    <CommandItem
      key={el.value}
      className={cn('text-left', disabled && 'opacity-50')}
      value={el.value}
      onSelect={() => handleSelect(el.value)}
    >
      <Check
        className={cn(
          'mr-2 h-4 w-4 min-w-4',
          multi
            ? Array.isArray(value) && value.includes(el.value)
              ? 'opacity-100'
              : 'opacity-0'
            : value === el.value
              ? 'opacity-100'
              : 'opacity-0',
        )}
      />
      {el.label}
    </CommandItem>
  ),
);
MemoizedCommandItem.displayName = 'MemoizedCommandItem';
